/* eslint-disable react-hooks/exhaustive-deps */
import { find } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import useResponseUser from '../../../../../../hooks/useResponseUser';
import { getFromLS } from '../../../../../../utils/utils';

const parseStoredUser = () => {
  try {
    return JSON.parse(getFromLS('userInfos') || '{}');
  } catch {
    return {};
  }
};

export const removeInlineColorStyles = (html: string): string => {
  if (!html) return html;
  return html.replace(/style="[^"]*color:[^;"']*;? ?[^"]*"/gi, '');
};

const useMinorTestResultPage = () => {
  const dataParams: any = useParams();
  const user = useMemo(parseStoredUser, []);
  const { getResponseTestUser, getTestVariables, getResponseUsers, getResponseTestUserGroupById } =
    useResponseUser();

  const [results, setResults] = useState<any[]>([]);
  const [test, setTest] = useState<any>();
  const [variables, setVariables] = useState<any[]>([]);
  const [userResponse, setUserResponse] = useState<any>();
  const [completedTestIds, setCompletedTestIds] = useState<number[]>([]);
  const [loadPage, setLoadPage] = useState(true);
  const [aglomerationsTraited, setAglomerationsTraited] = useState<any[]>([]);
  const [dataMax, setDataMax] = useState<any>(null);

  const diagramId = test?.attributes?.diagram?.data?.id;
  const isSimplePie = diagramId === 1;
  const isRadarDiagram = diagramId === 6;

  const canShowResult =
    Boolean(dataMax) || aglomerationsTraited?.some((el: any) => el?.matchedVariable);

  const sendDataMax = useCallback((data: any) => {
    setDataMax(data);
  }, []);

  useEffect(() => {
    (async () => {
      if (!dataParams.id) return;

      setLoadPage(true);

      const oneResponseTest = await getResponseTestUser(dataParams.id);
      const variableResponse = await getTestVariables(oneResponseTest.attributes.test.data.id);

      if (oneResponseTest.attributes.test.data.attributes.diagram.data.id === 6) {
        const groupedArrayAglo = Object.values(
          oneResponseTest?.attributes?.responses?.data?.reduce((acc: any, response: any) => {
            const aglo = response.attributes.question.data.attributes.aglomeration.data;
            const agloId = aglo.id;
            const agloName = aglo.attributes.name;
            const variableValue = response.attributes.variableValue || 0;

            if (!acc[agloId]) {
              acc[agloId] = {
                agloId,
                agloName,
                responses: [],
                totalVariableValue: 0,
              };
            }

            acc[agloId].responses.push(response);
            acc[agloId].totalVariableValue += variableValue;

            return acc;
          }, {})
        );

        const variablesToConcat =
          oneResponseTest?.attributes?.test?.data?.attributes?.aglomerations?.data?.map(
            (aglo: any) => {
              const grouped: any = groupedArrayAglo.find((g: any) => g.agloId === aglo.id);

              if (!grouped) {
                return {
                  ...aglo,
                  matchedVariable: null,
                };
              }

              const matchedVariable = aglo?.attributes?.variables?.data?.find(
                (v: any) =>
                  grouped?.totalVariableValue >= v?.attributes?.minInterval &&
                  grouped?.totalVariableValue <= v?.attributes?.maxInterval
              );

              return {
                ...aglo,
                totalVariableValue: grouped.totalVariableValue,
                matchedVariable: matchedVariable || null,
              };
            }
          );

        setAglomerationsTraited(
          variablesToConcat.sort((a: any, b: any) => b.totalVariableValue - a.totalVariableValue)
        );
      } else {
        setVariables(variableResponse.data);
        setUserResponse(oneResponseTest);
      }

      setTest(oneResponseTest.attributes.test.data);

      const allResponseUser = await getResponseTestUserGroupById(
        !dataParams.idUser ? user.id : dataParams.idUser,
        oneResponseTest.attributes.test.data.id
      );

      setResults(
        allResponseUser.map((result: any) => ({
          ...result,
          realization_date: result.attributes.createdAt,
        }))
      );

      try {
        const userAllResponses = await getResponseUsers(user.id);
        const completedIds = userAllResponses
          .map((response: any) => response?.attributes?.test?.data?.id)
          .filter((id: number) => id !== undefined && id !== null);

        setCompletedTestIds(Array.from(new Set(completedIds)) as number[]);
      } catch {
        setCompletedTestIds([]);
      }

      setLoadPage(false);
    })();
  }, [dataParams.id]);

  useEffect(() => {
    if (diagramId === 4 && userResponse && variables.length) {
      const sumScore =
        (userResponse?.attributes?.responses?.data as any[])?.reduce(
          (accumulator: number, resp: any) => accumulator + (resp?.attributes?.variableValue || 0),
          0
        ) || 0;

      const variableToShow = find(variables, (variable: any) => {
        return (
          variable?.attributes?.minInterval <= sumScore &&
          sumScore <= variable?.attributes?.maxInterval
        );
      });

      if (variableToShow) {
        sendDataMax({
          title: variableToShow.attributes.title,
          image: variableToShow.attributes.image,
          video: variableToShow.attributes.video,
          description: variableToShow.attributes.description,
          label: variableToShow.attributes.name,
        });
      }
    }
  }, [diagramId, userResponse, variables, sendDataMax]);

  const titrePage = test?.attributes?.resultTitle || test?.attributes?.title || '';
  const titreGraphique = test?.attributes?.diagramTitle || '';
  const univers = test?.attributes?.univers;

  const texteResultat =
    dataMax?.description ||
    (userResponse as any)?.attributes?.responses?.data?.[0]?.attributes?.variable?.data?.[0]
      ?.attributes?.description ||
    '';

  const historyItems = useMemo(
    () =>
      results.map(result => ({
        id: result.id,
        createdAt: result.attributes?.createdAt || result.realization_date,
      })),
    [results]
  );

  const recommendedTests = test?.attributes?.recommendedTests || [];
  const recommendedTestsOrder = test?.attributes?.recommendedTestsOrder || [];

  return {
    loadPage,
    user,
    dataParams,
    test,
    userResponse,
    variables,
    results,
    completedTestIds,
    aglomerationsTraited,
    dataMax,
    sendDataMax,
    canShowResult,
    isSimplePie,
    isRadarDiagram,
    diagramId,
    titrePage,
    titreGraphique,
    texteResultat,
    univers,
    historyItems,
    recommendedTests,
    recommendedTestsOrder,
    resultId: Number(dataParams.id),
  };
};

export default useMinorTestResultPage;
